import { useEffect, useRef, useState, useCallback } from 'react';
import { Howl } from 'howler';
import { ASSETS } from '../assetManifest.js';
import { CONSTANTS } from '../data.js';

const loadFromStorage = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed === typeof fallback) {
        return parsed;
      }
    }
  } catch {}
  return fallback;
};

export const useGameAudio = (currentEraKey: string) => {
  const [musicEnabled, setMusicEnabled] = useState<boolean>(() => loadFromStorage('time_alchemist_music_enabled', true));
  const [sfxEnabled, setSfxEnabled] = useState<boolean>(() => loadFromStorage('time_alchemist_sfx_enabled', true));
  
  const [musicVolume, setMusicVolume] = useState<number>(() => loadFromStorage('time_alchemist_music_vol', CONSTANTS.music.defaultVolume));
  const [sfxVolume, setSfxVolume] = useState<number>(() => loadFromStorage('time_alchemist_sfx_vol', CONSTANTS.sfx.defaultVolume));

  const musicMapRef = useRef<Record<string, Howl>>({});
  const sfxMapRef = useRef<Record<string, Howl>>({});
  const currentMusicKeyRef = useRef<string | null>(null);

  const musicFadeMs = typeof CONSTANTS.music?.crossfadeMs === 'number' ? CONSTANTS.music.crossfadeMs : 600;

  useEffect(() => {
    localStorage.setItem('time_alchemist_music_enabled', JSON.stringify(musicEnabled));
  }, [musicEnabled]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_sfx_enabled', JSON.stringify(sfxEnabled));
  }, [sfxEnabled]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_music_vol', JSON.stringify(musicVolume));
  }, [musicVolume]);

  useEffect(() => {
    localStorage.setItem('time_alchemist_sfx_vol', JSON.stringify(sfxVolume));
  }, [sfxVolume]);

  // Handle starting/stopping and crossfading era music
  useEffect(() => {
    const musicTargetVol = musicEnabled ? musicVolume : 0;

    if (!musicEnabled) {
      const prevKey = currentMusicKeyRef.current;
      if (prevKey && musicMapRef.current[prevKey]) {
        const prevHowl = musicMapRef.current[prevKey];
        try {
          prevHowl.fade(prevHowl.volume(), 0, musicFadeMs);
          setTimeout(() => {
            prevHowl.stop();
          }, musicFadeMs + 20);
        } catch {}
      }
      currentMusicKeyRef.current = null;
      return;
    }

    const currentMusicPath = ASSETS.music?.[currentEraKey]?.path;
    if (!currentMusicPath) return;

    const nextKey = currentEraKey;
    let nextHowl = musicMapRef.current[nextKey];
    if (!nextHowl) {
      nextHowl = new Howl({
        src: [currentMusicPath],
        loop: true,
        volume: 0 // Start at 0 volume, then fade in
      });
      musicMapRef.current[nextKey] = nextHowl;
    }

    const prevKey = currentMusicKeyRef.current;
    if (prevKey && prevKey !== nextKey) {
      const prevHowl = musicMapRef.current[prevKey];
      if (prevHowl) {
        try {
          prevHowl.fade(prevHowl.volume(), 0, musicFadeMs);
          setTimeout(() => prevHowl.stop(), musicFadeMs + 20);
        } catch {}
      }
    }

    currentMusicKeyRef.current = nextKey;

    if (!nextHowl.playing()) {
      try {
        const id = nextHowl.play();
        nextHowl.fade(0, musicTargetVol, musicFadeMs, id);
      } catch {}
    } else {
      try {
        nextHowl.fade(nextHowl.volume(), musicTargetVol, musicFadeMs);
      } catch {}
    }
  }, [musicEnabled, currentEraKey, musicFadeMs, musicVolume]);

  const playSoundEffect = useCallback((key: string) => {
    if (!sfxEnabled) return;

    const sfxPath = ASSETS.sfx?.[key]?.path;
    if (!sfxPath) {
      console.warn(`SFX with key "${key}" not found in asset manifest.`);
      return;
    }

    let sound = sfxMapRef.current[key];
    if (!sound) {
      sound = new Howl({
        src: [sfxPath],
        volume: sfxVolume,
      });
      sfxMapRef.current[key] = sound;
    }
    
    sound.volume(sfxVolume);

    try {
      sound.play();
    } catch (e) {
      console.error(`Error playing SFX "${key}":`, e);
    }
  }, [sfxEnabled, sfxVolume]);

  return { musicEnabled, setMusicEnabled, sfxEnabled, setSfxEnabled, musicVolume, setMusicVolume, sfxVolume, setSfxVolume, playSoundEffect };
};
